/**
 * Metadados dos estilos do widget de clima (compartilhado conceitualmente com o
 * admin — o admin tem cópia equivalente). Cada estilo tem uma PROPORÇÃO natural
 * (aspect = largura/altura visual). O editor trava o resize nessa proporção.
 *
 * Dia da semana / hora vêm do RELÓGIO LOCAL do player (não do servidor) e são
 * formatados sem `Intl`/locale (Tizen WebKit antigo falha com pt-BR) — arrays
 * fixos PT-BR indexados por getDay()/getMonth().
 */

export type WeatherStyleKey =
  | 'detailed'
  | 'vertical'
  | 'banner'
  | 'badge'
  | 'bar';
export type WeatherTheme = 'blue' | 'dark' | 'custom';

export const WEATHER_STYLE_KEYS: WeatherStyleKey[] = [
  'detailed',
  'vertical',
  'banner',
  'badge',
  'bar',
];

export interface WeatherStyleMeta {
  key: WeatherStyleKey;
  label: string;
  /** largura/altura VISUAL do card (px) — usado pra travar a proporção no editor */
  aspect: number;
}

export const WEATHER_STYLES: Record<WeatherStyleKey, WeatherStyleMeta> = {
  detailed: { key: 'detailed', label: 'Detalhado', aspect: 16 / 9 },
  vertical: { key: 'vertical', label: 'Vertical', aspect: 0.72 },
  banner: { key: 'banner', label: 'Banner', aspect: 3.2 },
  badge: { key: 'badge', label: 'Badge', aspect: 2.1 },
  // Barra horizontal (tira) — clima + localização + data/hora numa linha só.
  // aspect alto = larga e baixa (proporção de faixa/rodapé).
  bar: { key: 'bar', label: 'Barra', aspect: 16 },
};

export function resolveWeatherStyle(
  payload: Record<string, unknown> | null | undefined,
): WeatherStyleKey {
  const s = payload && typeof payload.style === 'string' ? payload.style : '';
  return (WEATHER_STYLE_KEYS as string[]).includes(s)
    ? (s as WeatherStyleKey)
    : 'badge';
}

export function resolveWeatherTheme(
  payload: Record<string, unknown> | null | undefined,
): WeatherTheme {
  const t = payload && typeof payload.theme === 'string' ? payload.theme : '';
  return t === 'dark' || t === 'custom' ? (t as WeatherTheme) : 'blue';
}

export function weatherBackground(
  theme: WeatherTheme,
  bgColor: string,
): string {
  if (theme === 'dark')
    return 'linear-gradient(160deg,#1b2440 0%,#2e3c63 100%)';
  if (theme === 'custom') return bgColor;
  return 'linear-gradient(135deg,#1e88e5 0%,#5eb3f6 100%)';
}

const DOW = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
];
const DOW_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MON_SHORT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

export function localDay(d: Date): string {
  return DOW[d.getDay()] ?? '';
}
export function localDayShort(d: Date): string {
  return DOW_SHORT[d.getDay()] ?? '';
}
export function localTime(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}
export function localDate(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')} ${MON_SHORT[d.getMonth()] ?? ''}`;
}
